import "@app/App.css";
import { Popover, PopoverContent, PopoverTrigger } from "@comps/ui/popover";
import { ObjectInfos } from "@dto/objects";
import { ReadRightAccess, ViewRightType, hasGlobalRightAccess } from "@dto/Users";
import { View } from "@dto/views";
import { updateStorageSelection } from "@utils/localStorage";
import { TW_ViewMenuButton, TW_ViewMenuButtonIcon } from "@utils/twClasses";
import { fileViewCopy } from "@utils/viewFolders";
import { EllipsisVertical } from "lucide-react";
import { useEffect, useState } from "react";
import { useModelStore, useUserStore } from "src/store";
import { useViewFolders } from "./useViewFolders";
import ViewMenuDeleteItem from "./ViewMenuDeleteItem";
import ViewMenuRenameItem from "./ViewMenuRenameItem";
import ViewMenuShareItem from "./ViewMenuShareItem";
import ViewMenuDuplicateItem from "./ViewMenuDuplicateItem";

interface ViewMenuButtonProps {
  object: ObjectInfos;
  view: View;
  showIcon: boolean;
}

function ViewMenuButton({ object: objectInfos, view, showIcon }: ViewMenuButtonProps) {

  const [isPopoverOpen, setIsPopoverOpen] = useState(false);
  const allObjectInfos = useModelStore((state) => state.objectInfos);
  const selectedObjectInfos = useModelStore((state) => state.selectedObjectInfos);
  const selectedView = useModelStore((state) => state.selectedView);
  const setObjectInfos = useModelStore((state) => state.updateObjectInfos);
  const sharedObjectInfos = useModelStore((state) => state.sharedObjectInfos);
  const [localView, setLocalView] = useState<View>(view);
  const workspace = useModelStore((state) => state.selectedWorkspace);
  const user = useUserStore((state) => state.user);
  const userRole = useModelStore((state) => state.userRole);
  const { prefs: folderPrefs, saveFolders } = useViewFolders(objectInfos.object._key);

  useEffect(() => {
    setLocalView(view);
  }, [view]);

  const deleteViewFromState = () => {
    const newObjectViews = objectInfos.views.filter(v => v._key !== view._key);
    const newObjectInfos = {
      ...objectInfos,
      views: newObjectViews
    };
    const newAllObjectInfos = allObjectInfos.map((o: ObjectInfos) => {
      if (o.object._key === objectInfos.object._key) {
        o = newObjectInfos;
      }
      return o;
    });

    let newSelectedObjectInfos = selectedObjectInfos;
    if (newSelectedObjectInfos?.object._key === objectInfos.object._key) {
      newSelectedObjectInfos = newObjectInfos;
    }
    let newSelectedView = selectedView;
    if (newSelectedView?._key === view._key) {
      let viewIndex = objectInfos.views.findIndex(v => v._key === view._key);
      if (newObjectViews.length > 0 && viewIndex > -1) {
        newSelectedView = newObjectViews[Math.max(0, viewIndex - 1)];
      } else {
        newSelectedView = null;
      }
    }
    setObjectInfos(newAllObjectInfos, newSelectedObjectInfos, newSelectedView, null, sharedObjectInfos);
    updateStorageSelection(workspace?._key, newSelectedObjectInfos?.object?._key, newSelectedView?._key, null);
  }


  const updateView = (view: View) => {
    const newObjectViews = objectInfos.views.map((v: View) => {
      if (v._key === view._key) {
        v = view;
      }
      return v;
    });
    const newObjectInfos = {
      ...objectInfos,
      views: newObjectViews
    };
    const newAllObjectInfos = allObjectInfos.map((o: ObjectInfos) => {
      if (o.object._key === objectInfos.object._key) {
        o = newObjectInfos;
      }
      return o;
    });
    let newSelectedObjectInfos = selectedObjectInfos;
    if (newSelectedObjectInfos?.object._key === objectInfos.object._key) {
      newSelectedObjectInfos = newObjectInfos;
    }
    let newSelectedView = selectedView;
    if (newSelectedView?._key === view._key) {
      newSelectedView = view;
    }
    setObjectInfos(newAllObjectInfos, newSelectedObjectInfos, newSelectedView, null, sharedObjectInfos);
  }

  const viewUpdated = (updatedView: View) => {
    const ownerChanged = localView.ownerKey !== updatedView.ownerKey;
    const isStillOwner = updatedView.ownerKey === user?._key;
    const isAdmin = user?.owner;

    if (ownerChanged && !isStillOwner && !isAdmin) {
      // Ownership transferred away — check if user still has access via sharing (direct or via role)
      const hasSharedAccess = hasGlobalRightAccess(user, userRole, ViewRightType, ReadRightAccess, updatedView._key);
      if (!hasSharedAccess) {
        // No remaining access — remove from sidebar
        deleteViewFromState();
      } else {
        // Still accessible via sharing — just update the view
        updateView(updatedView);
        setLocalView(updatedView);
      }
    } else {
      updateView(updatedView);
      setLocalView(updatedView);
    }
    setIsPopoverOpen(false);
  }

  const deleteView = () => {
    deleteViewFromState();
    setIsPopoverOpen(false);
  }

  const closeMenu = () => {
    setIsPopoverOpen(false);
  }

  function openMenu(e: React.MouseEvent<HTMLButtonElement>) {
    e.stopPropagation();
    setIsPopoverOpen(true);
  }

  const duplicateView = async (duplicatedView: View) => {
    // A view duplicated from a folder lands in the same folder, right after
    // the original; filed before the selection changes so that the sidebar
    // auto-expands the folder chain
    const filed = fileViewCopy(folderPrefs, view._key, duplicatedView._key);
    if (filed) {
      await saveFolders(filed);
    }

    const newViews = [...(objectInfos.views || []), duplicatedView];
    const newObjectInfo = { ...objectInfos, views: newViews };
    const newAllObjectInfos = allObjectInfos.map((o: ObjectInfos) => {
      if (o.object._key === objectInfos.object._key) {
        o = newObjectInfo;
      }
      return o;
    });
    setObjectInfos(newAllObjectInfos, newObjectInfo, duplicatedView, null, sharedObjectInfos);
    updateStorageSelection(workspace?._key, newObjectInfo.object?._key, duplicatedView?._key, null);
  }

  
  return (
    <Popover open={isPopoverOpen} onOpenChange={setIsPopoverOpen}>
      <PopoverTrigger className={TW_ViewMenuButton} onClick={openMenu}>
        <EllipsisVertical className={TW_ViewMenuButtonIcon + (showIcon ? " text-gray-400" : " text-gray-100")} />
      </PopoverTrigger>
      <PopoverContent className="w-max p-1">
        <ul>
          <li><ViewMenuRenameItem object={objectInfos.object} view={view} onViewUpdate={viewUpdated} closeMenu={closeMenu} /></li>
          <li><ViewMenuDuplicateItem objectInfos={objectInfos} view={view} onViewUpdate={duplicateView} closeMenu={closeMenu} /></li>
          <li><ViewMenuDeleteItem object={objectInfos.object} view={view} onViewDelete={deleteView} closeMenu={closeMenu} /></li>
          <li><ViewMenuShareItem object={objectInfos.object} view={view} onViewUpdate={viewUpdated} closeMenu={closeMenu} /></li>
        </ul>
      </PopoverContent>
    </Popover>
  )
}

export default ViewMenuButton;
